// src/pages/projectDetailTabs/WagesTab.tsx
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { WageEntry, Resource } from "../../data/mockData";

import { Card } from "../../components/ui/Card";
import { TableShell, CellClamp } from "../../components/ui/TableShell";

type Props = {
  wages: WageEntry[];
  resources: Resource[];
};

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * ISO week date range (Mon → Sun).
 * Week 1 is the week with Jan 4th in it.
 */
function getISOWeekRange(year: number, week: number) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7; // Sun=7
  const week1Mon = new Date(jan4);
  week1Mon.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));

  const start = new Date(week1Mon);
  start.setUTCDate(week1Mon.getUTCDate() + (week - 1) * 7);

  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);

  return { start, end };
}

function fmtShort(d: Date, locale?: string) {
  const yyyy = d.getUTCFullYear();
  const mm = d.getUTCMonth();
  const dd = d.getUTCDate();
  const local = new Date(yyyy, mm, dd);
  return local.toLocaleDateString(locale || undefined, { month: "short", day: "2-digit" });
}

function weekLabelWithDates(year: number, weekNumber: number, weekWord = "Week", locale?: string) {
  const { start, end } = getISOWeekRange(year, weekNumber);
  return `${year} — ${weekWord} ${String(weekNumber).padStart(2, "0")} (${fmtShort(start, locale)} – ${fmtShort(end, locale)})`;
}

export default function WagesTab({ wages, resources }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const ALL_WEEKS_KEY = "__ALL__";

  // ✅ useCallback so hooks can safely depend on it
  const getWorkerName = useCallback(
    (resourceId: string) =>
      resources.find((r) => r.id === resourceId)?.name || resourceId,
    [resources]
  );

  const totals = useMemo(() => {
    const total = wages.reduce((s, w) => s + (Number(w.totalWages) || 0), 0);
    const paid = wages
      .filter((w) => !!w.paid)
      .reduce((s, w) => s + (Number(w.totalWages) || 0), 0);
    const unpaid = wages
      .filter((w) => !w.paid)
      .reduce((s, w) => s + (Number(w.totalWages) || 0), 0);

    const sorted = wages
      .slice()
      .sort((a, b) => b.year - a.year || b.weekNumber - a.weekNumber);
    const latest = sorted[0]
      ? `${sorted[0].year}-W${String(sorted[0].weekNumber).padStart(2, "0")}`
      : "-";

    return { total, paid, unpaid, latest };
  }, [wages]);

  const weekRows = useMemo(() => {
    type WeekKey = string;
    const map = new Map<
      WeekKey,
      {
        year: number;
        weekNumber: number;
        total: number;
        paid: number;
        unpaid: number;
        workerIds: Set<string>;
        entryCount: number;
      }
    >();

    wages.forEach((w) => {
      const y = Number(w.year) || 0;
      const wk = Number(w.weekNumber) || 0;
      const key = `${y}-${wk}`;
      if (!map.has(key)) {
        map.set(key, {
          year: y,
          weekNumber: wk,
          total: 0,
          paid: 0,
          unpaid: 0,
          workerIds: new Set(),
          entryCount: 0,
        });
      }
      const row = map.get(key)!;
      const amt = Number(w.totalWages) || 0;
      row.total += amt;
      if (w.paid) row.paid += amt;
      else row.unpaid += amt;
      row.workerIds.add(w.resourceId);
      row.entryCount += 1;
    });

    const rows = Array.from(map.values()).sort(
      (a, b) => b.year - a.year || b.weekNumber - a.weekNumber
    );

    return rows.map((r) => {
      const status = r.unpaid <= 0 ? t("wages.paidBadge") : r.paid > 0 ? t("wages.partial") : t("wages.unpaidBadge");
      return {
        ...r,
        status,
        label: weekLabelWithDates(r.year, r.weekNumber, t("wages.weekLabel"), i18n.language),
        workers: r.workerIds.size,
      };
    });
  }, [wages, t, i18n.language]);

  const weekOptions = useMemo(() => {
    const base = weekRows.map((w) => ({
      key: `${w.year}-${w.weekNumber}`,
      label: w.label,
      year: w.year,
      weekNumber: w.weekNumber,
    }));

    // Add "All weeks" option at the top
    return [
      { key: ALL_WEEKS_KEY, label: t("wages.allWeeks"), year: 0, weekNumber: 0 },
      ...base,
    ];
  }, [weekRows, ALL_WEEKS_KEY, t]);

  const [selectedWeekKey, setSelectedWeekKey] = useState<string>(() => {
    if (weekRows.length) return ALL_WEEKS_KEY;
    return "";
  });

  React.useEffect(() => {
    if (!weekRows.length) {
      if (selectedWeekKey) setSelectedWeekKey("");
      return;
    }

    // If the current selection doesn't exist anymore, fall back to All weeks
    const exists = weekOptions.some((x) => x.key === selectedWeekKey);
    if (!exists) setSelectedWeekKey(ALL_WEEKS_KEY);
  }, [weekRows, weekOptions, selectedWeekKey, ALL_WEEKS_KEY]);

  const selectedWeek = useMemo(() => {
    if (selectedWeekKey === ALL_WEEKS_KEY) return null;
    const found = weekOptions.find((w) => w.key === selectedWeekKey);
    return found ? { year: found.year, weekNumber: found.weekNumber } : null;
  }, [weekOptions, selectedWeekKey, ALL_WEEKS_KEY]);

    const selectedWeekWorkerBreakdown = useMemo(() => {
    // If no week selected, treat as "All weeks"
    const filtered = selectedWeek
      ? wages.filter((w) => w.year === selectedWeek.year && w.weekNumber === selectedWeek.weekNumber)
      : wages.slice();

    const byRes: Record<
      string,
      {
        resourceId: string;
        workerName: string;
        days: number;
        total: number;
        paidCount: number;
        entryCount: number;
      }
    > = {};

    filtered.forEach((w) => {
      const id = w.resourceId;
      if (!byRes[id]) {
        byRes[id] = {
          resourceId: id,
          workerName: getWorkerName(id),
          days: 0,
          total: 0,
          paidCount: 0,
          entryCount: 0,
        };
      }
      byRes[id].days += Number(w.daysWorked) || 0;
      byRes[id].total += Number(w.totalWages) || 0;
      byRes[id].paidCount += w.paid ? 1 : 0;
      byRes[id].entryCount += 1;
    });

    return Object.values(byRes).sort((a, b) => b.total - a.total);
  }, [wages, selectedWeek, getWorkerName]);

  const sortedEntries = useMemo(() => {
    return wages.slice().sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      if (a.weekNumber !== b.weekNumber) return b.weekNumber - a.weekNumber;
      return (b.id || "").localeCompare(a.id || "");
    });
  }, [wages]);

  const goEnterTime = () => navigate("/wages"); // adjust if your route differs

  return (
    <div className="container" style={{ padding: 0 }}>
      <style>{`
        .wCardGrid{
          display:grid;
          gap:10px;
          grid-template-columns:repeat(4, minmax(0, 1fr));
        }
        @media (max-width: 980px){
          .wCardGrid{ grid-template-columns:repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 680px){
          .wCardGrid{ grid-template-columns:1fr; }
        }
        .wMiniRow{
          display:flex;
          justify-content:space-between;
          align-items:baseline;
          padding:8px 10px;
          border-radius:14px;
          border:1px solid rgba(15,23,42,0.08);
          background:rgba(255,255,255,0.70);
        }
        .wMiniLabel{ color:var(--muted); font-weight:900; font-size:12px; }
        .wMiniValue{ font-weight:1000; letter-spacing:-0.2px; }
        .numRight{ text-align:right; }

        .wSummaryHead{
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:10px;
          flex-wrap:wrap;
        }
        .wSummaryTitle{
          display:flex;
          flex-direction:column;
          gap:2px;
        }
        .wSummaryTitleTop{
          font-size: 16px;
          font-weight: 1000;
          letter-spacing: -0.2px;
        }
        .wSummaryTitleSub{
          color: var(--muted);
          font-size: 12px;
          font-weight: 800;
        }
      `}</style>

      <div style={{ marginTop: 0 }}>
        <Card
          title={
            <div className="wSummaryHead">
              <div className="wSummaryTitle">
                <div className="wSummaryTitleTop">{t("wages.tab.summary")}</div>
                <div className="wSummaryTitleSub">{t("wages.tab.summarySub")}</div>
              </div>

              <button className="btn btnSm" type="button" onClick={goEnterTime}>
                {t("wages.tab.enterTime")}
              </button>
            </div>
          }
          subtitle={null as any}
        >
          <div className="wCardGrid">
            <div className="wMiniRow">
              <div className="wMiniLabel">{t("wages.tab.totalWages")}</div>
              <div className="wMiniValue">{money(totals.total)}</div>
            </div>

            <div className="wMiniRow">
              <div className="wMiniLabel">{t("wages.tab.unpaidWages")}</div>
              <div className={`wMiniValue ${totals.unpaid > 0 ? "numWarn" : "numGood"}`}>
                {money(totals.unpaid)}
              </div>
            </div>

            <div className="wMiniRow">
              <div className="wMiniLabel">{t("wages.tab.paidWages")}</div>
              <div className="wMiniValue">{money(totals.paid)}</div>
            </div>

            <div className="wMiniRow">
              <div className="wMiniLabel">{t("wages.tab.latestWeekLogged")}</div>
              <div className="wMiniValue">{totals.latest}</div>
            </div>
          </div>

          <div className="noteOnCard" style={{ marginTop: 10 }}>
            {t("wages.tab.enterTimeTip")}
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <TableShell title={t("wages.tab.weeklySummaryTitle")} subtitle={t("wages.tab.weeklySummarySub")}>
          {weekRows.length === 0 ? (
            <div style={{ color: "var(--muted)", fontStyle: "italic", padding: "8px 2px" }}>
              {t("wages.tab.noWageEntries")}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t("common.week")}</th>
                  <th style={{ width: 110 }} className="numRight">{t("wages.tab.workers")}</th>
                  <th style={{ width: 110 }} className="numRight">{t("wages.tab.entries")}</th>
                  <th style={{ width: 140 }} className="numRight">{t("common.total")}</th>
                  <th style={{ width: 140 }} className="numRight">{t("common.unpaid")}</th>
                  <th style={{ width: 140 }} className="numRight">{t("common.paid")}</th>
                  <th style={{ width: 120 }}>{t("common.status")}</th>
                </tr>
              </thead>
              <tbody>
                {weekRows.map((w) => (
                  <tr key={`${w.year}-${w.weekNumber}`}>
                    <td>
                      <CellClamp lines={2} title={w.label}>
                        <span style={{ fontWeight: 900 }}>{w.label}</span>
                      </CellClamp>
                    </td>
                    <td className="numRight">{w.workers}</td>
                    <td className="numRight">{w.entryCount}</td>
                    <td className="numRight" style={{ fontWeight: 900 }}>
                      {money(w.total)}
                    </td>
                    <td className="numRight" style={{ fontWeight: 900 }}>
                      <span className={w.unpaid > 0 ? "numWarn" : "numGood"}>
                        {money(w.unpaid)}
                      </span>
                    </td>
                    <td className="numRight">{money(w.paid)}</td>
                    <td>
                      <span className={`badge ${w.status === "Paid" ? "badgeGood" : "badgeWarn"}`}>
                        {w.status === "Paid" ? t("common.paid") : w.status === "Unpaid" ? t("common.unpaid") : t("wages.tab.partial")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TableShell>
      </div>

      <div style={{ marginTop: 14 }}>
        <TableShell
          title={t("wages.tab.selectedWeekBreakdown")}
          subtitle={t("wages.tab.selectedWeekBreakdownSub")}
          right={
            <div style={{ minWidth: 320 }}>
              <select
                value={selectedWeekKey}
                onChange={(e) => setSelectedWeekKey(e.target.value)}
                style={{ width: "100%" }}
                disabled={weekOptions.length === 0}
              >
                {weekOptions.length === 0 ? <option value="">{t("wages.tab.noWeeksYet")}</option> : null}
                {weekOptions.map((w) => (
                  <option key={w.key} value={w.key}>
                    {w.label}
                  </option>
                ))}
              </select>
            </div>
          }
        >
          {selectedWeekWorkerBreakdown.length === 0 ? (
            <div style={{ color: "var(--muted)", fontStyle: "italic", padding: "8px 2px" }}>
              {t("wages.tab.noSelectedWeekEntries")}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t("common.worker")}</th>
                  <th style={{ width: 120 }} className="numRight">{t("wages.tab.totalDays")}</th>
                  <th style={{ width: 150 }} className="numRight">{t("wages.tab.totalWages")}</th>
                  <th style={{ width: 150 }} className="numRight">{t("wages.tab.paidEntries")}</th>
                </tr>
              </thead>
              <tbody>
                {selectedWeekWorkerBreakdown.map((r) => (
                  <tr key={r.resourceId}>
                    <td style={{ fontWeight: 900 }}>{r.workerName}</td>
                    <td className="numRight">{r.days}</td>
                    <td className="numRight" style={{ fontWeight: 900 }}>
                      {money(r.total)}
                    </td>
                    <td className="numRight">
                      {r.paidCount}/{r.entryCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TableShell>
      </div>

      <div style={{ marginTop: 14 }}>
        <TableShell title={t("wages.tab.entriesTitle")} subtitle={t("wages.tab.entriesSubtitle", { count: sortedEntries.length })}>
          {sortedEntries.length === 0 ? (
            <div style={{ color: "var(--muted)", fontStyle: "italic", padding: "8px 2px" }}>
              {t("wages.tab.noWageEntries")}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t("common.worker")}</th>
                  <th>{t("common.week")}</th>
                  <th style={{ width: 90 }} className="numRight">{t("common.days")}</th>
                  <th style={{ width: 140 }} className="numRight">{t("common.total")}</th>
                  <th style={{ width: 110 }}>{t("common.paid")}</th>
                  <th style={{ width: 130 }}>{t("wages.colPaidDate")}</th>
                  <th style={{ width: 130 }}>{t("wages.tab.paidMethod")}</th>
                  <th>{t("common.notes")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map((w) => (
                  <tr key={w.id}>
                    <td style={{ fontWeight: 900 }}>{getWorkerName(w.resourceId)}</td>
                    <td>
                      <CellClamp lines={2} title={weekLabelWithDates(w.year, w.weekNumber, t("wages.weekLabel"), i18n.language)}>
                        {weekLabelWithDates(w.year, w.weekNumber, t("wages.weekLabel"), i18n.language)}
                      </CellClamp>
                    </td>
                    <td className="numRight">{w.daysWorked}</td>
                    <td className="numRight" style={{ fontWeight: 900 }}>
                      {money(w.totalWages)}
                    </td>
                    <td>
                      <span className={`badge ${w.paid ? "badgeGood" : "badgeWarn"}`}>
                        {w.paid ? t("common.paid") : t("common.unpaid")}
                      </span>
                    </td>
                    <td>{(w as any).paidDate || "—"}</td>
                    <td>{(w as any).paidMethod || "—"}</td>
                    <td>
                      <CellClamp lines={2} title={w.description || ""}>
                        {w.description || <span style={{ color: "var(--muted)" }}>—</span>}
                      </CellClamp>
                    </td>
                  </tr>
                ))}

                <tr>
                  <td colSpan={3} style={{ textAlign: "right", fontWeight: 1000 }}>
                    {t("common.total")}
                  </td>
                  <td className="numRight" style={{ fontWeight: 1000 }}>
                    {money(totals.total)}
                  </td>
                  <td colSpan={4} />
                </tr>
              </tbody>
            </table>
          )}
        </TableShell>

        <div className="noteOnCard" style={{ marginTop: 10 }}>
          {t("wages.tab.weeklyTip")}
        </div>
      </div>
    </div>
  );
}
